import dayjs from 'dayjs';  // Make sure you have dayjs installed (npm install dayjs)
import duration from 'dayjs/plugin/duration';
import React, { useState, useEffect } from "react";
import {
  Typography,
  Autocomplete,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert, 
  AlertTitle,
  List, 
  ListItem,
  Chip
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

import { postJobOrder } from "../API/jobOrderAPI";
import { getCacheClients, getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";

import { useNavigate } from "react-router-dom";
import WorkIcon from "@mui/icons-material/Work";
import BadgeIcon from "@mui/icons-material/Badge";
import { customAlphabet } from 'nanoid';

import { 
  JOB_TYPES,
  JOB_DESCRIPTIONS,
  DOCUMENTS_TO_BRING,
  EQUIPMENT
} from "./utils/JobOrderConstants";
import { validateJobOrder } from './utils/JobOrderValidation';
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

dayjs.extend(duration);

const useFetchTechnicians = () => {
  const [technicians, setTechnicians] = useState(null); // State to store fetched data

  useEffect(() => {
    getTechnicians()
      .then((data) => {
        const sortedTechnicians = data.sort((a, b) =>
          a.lastname.localeCompare(b.lastname)
        );
        setTechnicians(sortedTechnicians); // Set the state with sorted data
      })
      .catch((error) => console.error("Error fetching technicians:", error));
  }, []);

  return technicians;
};

// Function to generate list of recurrence dates
function generateRecurrenceDates(startDate, startTime, endTime, recurrence) {
  const dates = [];
  let currentDate = dayjs(`${startDate} ${startTime}`);
  const interval = recurrence.interval || 1;
  const count = parseInt(recurrence.count, 10) || 1;

  for (let i = 0; i < count; i++) {
    const endDateTime = currentDate.add(dayjs.duration({ hours: 2 }));  // Calculate end time

    dates.push({
      date: currentDate.format('YYYY-MM-DD'),
      start_time: currentDate.format('HH:mm'),
      end_time: endDateTime.format('HH:mm'),
    });

    // Increment by recurrence frequency
    switch (recurrence.frequency) {
      case 'daily':
        currentDate = currentDate.add(interval, 'day');
        break;
      case 'weekly':
        currentDate = currentDate.add(interval, 'week');
        break;
      case 'monthly':
        currentDate = currentDate.add(interval, 'month');
        break;
      case 'quarterly':
        currentDate = currentDate.add(interval * 3, 'month');
        break;
      case 'yearly':
        currentDate = currentDate.add(interval, 'year');
        break;
      default:
        currentDate = currentDate.add(interval, 'day');
        break;
    }
  }

  return dates;
}

function CreateJobOrder() {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const generateId = customAlphabet(alphabet, 8); // Creates a function to generate an 8-character ID
  const navigate = useNavigate();

  const clientFilter = createFilterOptions({
    matchFrom: "any",                     // allow substring matches, not just prefix
    stringify: (option) => option?.name || "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [uiState, setUIState] = useState({
    isLoading: false,
    isFetchingClient: false,
  });

  const [error, setError] = useState(null);

  const [clientsCache, setClientsCache] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    const fetchClientsCache = async () => {
      try {
        setClientsLoading(true);
        const fetchedClients = await getCacheClients();
        setClientsCache(fetchedClients);
      } catch (err) {
        console.error("Error fetching clients cache:", err);
      } finally {
        setClientsLoading(false);
      }
    };
    fetchClientsCache();
  }, []);

  const [client, setClient] = useState(null);

  const [jobOrder, setJobOrder] = useState({
    client_id: "",
    client: { id: "", name: "", address: {}, contact: {} },
    job_types: [],
    job_description: [],
    special_notes: '',
    job_date: "",
    start_time: "",
    end_time: "",
    technicians: [],
    status: "Not Done",
    service_report_status: "Not Done",
    billing_record_status: "Not Done",
    endorser_name: "",
    endorser_contact_number: "",
    current_status: "",
    documents_to_bring: [],
    payment_method: "",
    schedule_confirmed: "",
    client_complaints: "",
    equipment: [],
    form_type: "",
    appointment_type: "",
    treatment_type: "",
  });

  const technicians = useFetchTechnicians();

  const [modalOpen, setModalOpen] = useState(false);

  // Opens the modal
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  // Closes the modal
  const handleDialogClose = () => {
    setModalOpen(false);
  };

  const handleCloseAssignment = () => {
    setModalOpen(false);
  };

  const handleAddressChange = (selectedAddress) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        address: selectedAddress, // Update the address field with the new object
      },
    }));
  };

  const handleContactChange = (selectedContact) => {
    const updatedContact = selectedContact
      ? {
        ...selectedContact,
        number:
          selectedContact.numbers?.length === 1
            ? selectedContact.numbers[0]
            : "",
        email:
          selectedContact.emails?.length === 1
            ? selectedContact.emails[0]
            : "",
      }
      : {};

    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        contact: updatedContact, // Update the contact field with the new object
      },
    }));
  };

  const handleContactNumberChange = (selectedNumber) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        contact: {
          ...prev.client.contact, // Preserve other properties in contact
          number: selectedNumber, // Update the number field with the new object
        },
      },
    }));
  };

  const handleContactEmailChange = (selectedEmail) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        contact: {
          ...prev.client.contact, // Preserve other properties in contact
          email: selectedEmail, // Update the email field with the selected object
        },
      },
    }));
  };

  const handleJobOrderSpecialNotesChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handleJobOrderScheduleChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handleSearchClient = (event, value) => {
    setJobOrder((prevJobOrder) => ({
      ...prevJobOrder, // Preserve other properties in jobOrder
      client_id: value ? value.id : "", // Update client_id based on value
      client: value
        ? {
          ...prevJobOrder.client, // Preserve other client properties if needed
          id: value.id,
          name: value.name,
        }
        : {
          id: "",
          name: "",
          address: {},
          contact: {}, // Corrected to `contact` as per your new structure
        },
    }));

    if (value === null) {
      // Set client to null only when value is null
      setClient(null); // Directly update the `client` state
    }
  };

  const handleSearchButtonClick = async () => {
    if (!jobOrder.client.id) {
      console.log("No client selected");
      return;
    }

    if (uiState.isFetchingClient) {
      console.log("Already fetching client details...");
      return;
    }

    setUIState((prev) => ({
      ...prev,
      isLoading: true,
      isFetchingClient: true,
    }));

    try {
      const fetchedClient = await getClient(jobOrder.client.id);
      setClient(fetchedClient);

      // If client requires work permit, pre-check "Work Permit / Gate Pass" in Required Documents (still editable)
      if (fetchedClient?.work_permit_required) {
        const wpKey = "Work Permit / Gate Pass".toLowerCase().replace(/\s+/g, "_");
        setJobOrder((prev) => {
          if ((prev.documents_to_bring || []).includes(wpKey)) return prev;
          return {
            ...prev,
            documents_to_bring: [...(prev.documents_to_bring || []), wpKey],
          };
        });
      }

      // Logic to prepopulate jobOrder.client
      const updatedClient = { ...jobOrder.client };

      // Assign address if only one exists
      if (fetchedClient.addresses?.length === 1) {
        updatedClient.address = fetchedClient.addresses[0];
      } else {
        updatedClient.address = {};
      }

      if (fetchedClient.contacts?.length === 1) {
        const singleContact = fetchedClient.contacts[0];
        updatedClient.contact = {
          ...singleContact,
          number:
            singleContact.numbers?.length === 1 ? singleContact.numbers[0] : "",
          email:
            singleContact.emails?.length === 1 ? singleContact.emails[0] : "",
        };
      } else {
        updatedClient.contact = {};
      }

      // Update jobOrder with the updated client details
      setJobOrder((prev) => ({
        ...prev,
        client: updatedClient,
      }));
    } catch (error) {
      console.error("Error fetching client details:", error);
    } finally {
      setUIState((prev) => ({
        ...prev,
        isLoading: false,
        isFetchingClient: false,
      }));
    }
  };

  const [recurrence, setRecurrence] = useState({
    toggle: false,
    frequency: '',
    interval: 1,
    count: ''
  });

  const handleRecurrenceChange = (key, value) => {
    setRecurrence((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const getUnitLabel = () => {
    switch (recurrence.frequency) {
      case 'daily':
        return 'day(s)';
      case 'weekly':
        return 'week(s)';
      case 'monthly':
        return 'month(s)';
      case 'quarterly':
        return 'quarter(s)';
      case 'yearly':
        return 'year(s)';
      default:
        return '';
    }
  };

  const handleJobTypesChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedJobTypes = isChecked
        ? [...prev.job_types, formattedOption] // Add formattedOption if checked
        : prev.job_types.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedJobTypes === prev.job_types) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        job_types: updatedJobTypes,
      };
    });
  };

  const handleJobDescriptionChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedJobDescription = isChecked
        ? [...prev.job_description, formattedOption] // Add formattedOption if checked
        : prev.job_description.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedJobDescription === prev.job_description) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        job_description: updatedJobDescription,
      };
    });
  };

  const handleEndorserNameChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handleEndorserContactNumberChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handleCurrentStatusChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handlePaymentMethodChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the payment_method field
    }));
  };

  const handleDocumentsToBringChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedDocumentsToBring = isChecked
        ? [...prev.documents_to_bring, formattedOption] // Add formattedOption if checked
        : prev.documents_to_bring.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedDocumentsToBring === prev.documents_to_bring) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        documents_to_bring: updatedDocumentsToBring,
      };
    });
  };


  const handleEquipmentChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedEquipment = isChecked
        ? [...prev.equipment, formattedOption] // Add formattedOption if checked
        : prev.equipment.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedEquipment === prev.equipment) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        equipment: updatedEquipment,
      };
    });
  };

  const handleScheduleConfirmedChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the payment_method field
    }));
  };

  const handleClientComplaintsChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the payment_method field
    }));
  };

  const [selectedTechIds, setSelectedTechIds] = useState([]);

  const handleTechCheckboxChange = (techId) => (event) => {
    // Prevent the event from bubbling up and triggering the dialog's onClose
    event.stopPropagation();
    if (event.target.checked) {
      setSelectedTechIds((prev) => [...prev, techId]);
    } else {
      setSelectedTechIds((prev) => prev.filter((id) => id !== techId));
    }
  };

  const handleTechAssign = () => {
    // First, clear technicians from the job order
    setJobOrder((prevJobOrder) => ({
      ...prevJobOrder,
      technicians: [],
    }));

    setJobOrder((prevJobOrder) => ({
      ...prevJobOrder,
      technicians: technicians
        .filter((tech) => selectedTechIds.includes(tech.id))
        .map(({ id, firstname, lastname }) => ({ id, firstname, lastname })),
    }));

    handleCloseAssignment();
  };

  // job order form validation
  const [errors, setErrors] = useState([]);

  // whenever errors show up, clear them after 5s
  useEffect(() => {
    if (errors.length === 0) return;
    const timer = setTimeout(() => setErrors([]), 7000);
    return () => clearTimeout(timer);
  }, [errors]);


  async function handleSubmit(e) {
    e.preventDefault();
    
    const storedUser = localStorage.getItem("user");
    const user = storedUser ? JSON.parse(storedUser).username : "";

    // use Yup helper
    const validationErrors = await validateJobOrder(jobOrder);
    if (validationErrors.length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setErrorMessage("");
      setIsLoading(true);

      if (recurrence.toggle) {
        // Generate recurrence dates
        const recurrenceDates = generateRecurrenceDates(
          jobOrder.job_date,
          jobOrder.start_time,
          jobOrder.end_time,
          recurrence
        );

        const recurrence_id = generateId()

        const jobOrders = recurrenceDates.map((dateTime) => ({
          ...jobOrder,
          job_date: dateTime.date,
          start_time: dateTime.start_time,
          end_time: dateTime.end_time,
          recurrence_id: recurrence_id,
          recurrence_frequency: recurrence.frequency,
          recurrence_interval: recurrence.interval,
          recurrence_count: recurrence.count,
          user_create: user,
          user_update: user,
        }));

        // Loop through and submit each job order (or submit as batch)
        for (const order of jobOrders) {
          await postJobOrder(order);
        }
      } else {
        const jobOrderWithUser = {
          ...jobOrder,
          user_create: user,
          user_update: user,
        };

        await postJobOrder(jobOrderWithUser);
      }


      setSuccessMessage("Job Order created successfully!");

      // ── CLEAR ALL FIELDS ──
      setJobOrder({
        client_id: "",
        client: { id: "", name: "", address: {}, contact: {} },
        job_types: [],
        job_description: [],
        special_notes: "",
        job_date: "",
        start_time: "",
        end_time: "",
        technicians: [],
        status: "Not Done",
        service_report_status: "Not Done",
        billing_record_status: "Not Done",
        endorser_name: "",
        endorser_contact_number: "",
        current_status: "",
        documents_to_bring: [],
        payment_method: "",
        schedule_confirmed: "",
        client_complaints: "",
        equipment: [],
        form_type: ""
      });
      setClient(null);
      setRecurrence({ toggle: false, frequency: "", interval: 1, count: "" });
      setSelectedTechIds([]);
    } catch (error) {
      setErrorMessage(
        `An error occurred while creating the Job Order: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <BaseFormWrapper
      titleIcon={<WorkIcon />}
      title="Operations"
      subTitle="Manage Job Orders"
      subTitleRoute="/manage-job-orders"
      formTitle="Create Job Order"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit} className="job-order-form">        
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "10px", marginBottom: "10px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Client Information
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={clientsCache}
                      getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, params) => clientFilter(options, params)}
                      openOnFocus                                                           
                      autoHighlight
                      loading={clientsLoading}
                      noOptionsText={clientsLoading ? "Loading..." : "No clients found"}
                      value={
                        jobOrder.client.name
                          ? {
                            id: jobOrder.client.id,
                            name: jobOrder.client.name,
                          }
                          : null
                      } // Controlled component tied to jobOrder.client
                      onChange={(event, newValue) =>
                        handleSearchClient(event, newValue)
                      } // Updates jobOrder.client
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <div style={{display:'flex',flexDirection:'column'}}>
                            <span>{option.name}</span>
                            <small style={{opacity:.7}}>ID: {option.id}</small>
                          </div>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client Name"
                          variant="outlined"
                          fullWidth
                          error={!!error} // Displays error styling if there is an error
                          helperText={error || ""} // Displays error message
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={4} container alignItems="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearchButtonClick} // Call button handler
                      disabled={uiState.isLoading} // Disable button while loading
                    >
                      {uiState.isLoading ? "Searching..." : "Search"}{" "}
                      {/* Change button label dynamically */}
                    </Button>
                  </Grid>
                  {/* Client ID + Work Permit Stamp */}
                  <Grid item xs={12}>
                    <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                      <Grid item xs>
                        <TextField
                          label="Client ID"
                          value={jobOrder.client.id || ""}
                          size="small"
                          fullWidth
                          disabled
                          sx={{ maxWidth: "320px" }}
                        />
                      </Grid>

                      <Grid item>
                        {client?.work_permit_required ? (
                          <Chip
                            icon={<BadgeIcon sx={{ fontSize: 18 }} />}
                            label="WORK PERMIT REQUIRED"
                            size="small"
                            variant="outlined"                            
                            sx={{
                              borderColor: "rgba(25, 118, 210, 0.6)",   // MUI primary blue
                              color: "#1976d2",
                              bgcolor: "rgba(25, 118, 210, 0.08)",
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                              height: 30,
                            }}
                          />
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Job Site Address */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={10}>
                        <FormControl
                          fullWidth
                          disabled={isLoading || !client}
                        >
                          <InputLabel id="job-site-address-label">
                            Job Site Address
                          </InputLabel>
                          <Select
                            labelId="job-site-address"
                            value={jobOrder.client.address?.id || ""}
                            onChange={(e) => {
                              const selectedAddress =
                                client?.addresses?.find(
                                  (address) =>
                                    address.id === e.target.value
                                );
                              handleAddressChange(selectedAddress); // Pass the full address object
                            }}
                            label="Job Site Address"
                          >
                            {(client?.addresses || []).map(
                              ({
                                id,
                                house_unit_street,
                                barangay,
                                city,
                                metro_province,
                                zipcode,
                              }) => (
                                <MenuItem key={id} value={id}>
                                  {`${house_unit_street || ""}, ${barangay || ""}, ${city || ""}, ${metro_province || ""} ${zipcode || ""}`}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          name="area"
                          label="Area (sqm)"
                          value={jobOrder.client.address?.area || ""}
                          fullWidth
                          disabled={
                            isLoading || !jobOrder.client.address?.id
                          } // Disable if no address is selected
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Contact Person */}
                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      disabled={isLoading || !client}
                    >
                      <InputLabel id="contact-person-label">
                        Contact Person
                      </InputLabel>
                      <Select
                        labelId="contact-person-label"
                        value={jobOrder.client.contact?.id || ""}
                        onChange={(e) => {
                          const selectedContact =
                            client?.contacts?.find(
                              (contact) => contact.id === e.target.value
                            );
                          handleContactChange(selectedContact); // Pass the full contact object
                        }}
                        label="Contact Person"
                      >
                        {(client?.contacts || []).map(
                          ({ id, firstname, lastname }) => (
                            <MenuItem key={id} value={id}>
                              {`${lastname}, ${firstname}`}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Contact Email */}
                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      disabled={
                        isLoading || !client || !jobOrder.client.contact
                      }
                    >
                      <InputLabel id="contact-email-label">
                        Contact Email
                      </InputLabel>
                      <Select
                        labelId="contact-email-label"
                        value={jobOrder.client.contact?.email?.id || ""}
                        onChange={(e) => {
                          const selectedEmail =
                            jobOrder.client.contact.emails.find(
                              (email) => email.id === e.target.value
                            );
                          handleContactEmailChange(selectedEmail); // Pass the full email object
                        }}
                        label="Contact Email"
                      >
                        {(jobOrder.client.contact?.emails || []).map(
                          (email) => (
                            <MenuItem key={email.id} value={email.id}>
                              {email.email}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Contact Number */}
                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      disabled={
                        isLoading || !client || !jobOrder.client.contact
                      }
                    >
                      <InputLabel id="contact-number-label">
                        Contact Number
                      </InputLabel>
                      <Select
                        labelId="contact-number-label"
                        value={
                          jobOrder.client.contact?.number?.id || ""
                        }
                        onChange={(e) => {
                          const selectedNumber =
                            jobOrder.client.contact.numbers.find(
                              (number) => number.id === e.target.value
                            );
                          handleContactNumberChange(selectedNumber); // Pass the full number object
                        }}
                        label="Contact Number"
                      >
                        {(jobOrder.client.contact?.numbers || []).map(
                          (number) => (
                            <MenuItem key={number.id} value={number.id}>
                              {number.type}: {number.number}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Category"
                      variant="outlined"
                      value={client?.category || ''}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Parent Client"
                      variant="outlined"
                      value={client?.parent_client?.name || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>

                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Job Type
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...JOB_TYPES]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option
                      .toLowerCase()
                      .replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={jobOrder.job_types.includes(formattedOption)}
                            onChange={(e) =>
                              handleJobTypesChange(e, formattedOption)
                            }
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>

            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Job Description
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...JOB_DESCRIPTIONS]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={jobOrder.job_description.includes(formattedOption)}
                            onChange={(e) => handleJobDescriptionChange(e, formattedOption)}
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography

                  >
                    Special Notes
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder.special_notes || ""}
                      onChange={(e) =>
                        handleJobOrderSpecialNotesChange(
                          "special_notes",
                          e.target.value
                        )
                      }
                      fullWidth
                      disabled={isLoading}
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Schedule Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Schedule
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent
              >
                <Grid container spacing={3}>
                  <Grid item xs={2}>
                    <TextField
                      label="Date"
                      name="job_date"
                      type="date"
                      value={jobOrder.job_date || ""}
                      onChange={(e) =>
                        handleJobOrderScheduleChange(
                          "job_date",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Start Time"
                      name="start_time"
                      type="time"
                      value={jobOrder.start_time || ""}
                      onChange={(e) =>
                        handleJobOrderScheduleChange(
                          "start_time",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="End Time"
                      name="end_time"
                      type="time"
                      value={jobOrder.end_time}
                      onChange={(e) =>
                        handleJobOrderScheduleChange(
                          "end_time",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={2}>
                        <FormControl fullWidth>
                          <InputLabel>Recurrence</InputLabel>
                          <Select
                            value={recurrence.toggle ? 'true' : 'false'}
                            onChange={(e) => handleRecurrenceChange('toggle', e.target.value === 'true')}
                            label="Recurrence"
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {recurrence.toggle && (
                        <>
                          <Grid item xs={2}>
                            <FormControl fullWidth>
                              <InputLabel>Frequency</InputLabel>
                              <Select
                                value={recurrence.frequency}
                                onChange={(e) => handleRecurrenceChange('frequency', e.target.value)}
                                label="Frequency"
                              >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="quarterly">Quarterly</MenuItem>
                                <MenuItem value="yearly">Yearly</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={3} container alignItems="center" spacing={1}>
                            <Grid item>
                              <Typography>Every</Typography>
                            </Grid>
                            <Grid item xs>
                              <TextField
                                fullWidth
                                type="number"
                                label="Interval"
                                value={recurrence.interval}
                                onChange={(e) => handleRecurrenceChange('interval', e.target.value)}
                                InputProps={{ inputProps: { min: 1 } }}
                              />
                            </Grid>
                            <Grid item>
                              <Typography>{getUnitLabel()}</Typography>
                            </Grid>
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Count"
                              value={recurrence.count}
                              onChange={(e) => handleRecurrenceChange('count', e.target.value)}
                              InputProps={{ inputProps: { min: 1 } }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid item xs={12} md={12} sx={{ display: "flex", flexDirection: "column" }}>
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", textTransform: "uppercase", fontSize: "1rem" }}
                  >
                    Appointment
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {/* Appointment Type */}
                  <Grid item xs={12} md={12} lg={12} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">Appointment Type</InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        name="appointment_type"
                        value={jobOrder.appointment_type}
                        label="appointment_type"
                        onChange={(e) =>
                          setJobOrder(prev => ({ ...prev, appointment_type: e.target.value }))
                        }
                      >
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Treatment Type */}
                  <Grid item xs={12} md={12} lg={12} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">Treatment Type</InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        name="treatment_type"
                        value={jobOrder.treatment_type}
                        label="treatment_type"
                        onChange={(e) =>
                          setJobOrder(prev => ({ ...prev, treatment_type: e.target.value }))
                        }
                      >
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Endorsement Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Endorsement Form
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent
              >
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Name"
                      name="endorser_name"
                      type="text"
                      value={jobOrder.endorser_name || ""}
                      onChange={(e) =>
                        handleEndorserNameChange(
                          "endorser_name",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Contact Number"
                      name="endorser_contact_number"
                      type="text"
                      value={jobOrder.endorser_contact_number || ""}
                      onChange={(e) =>
                        handleEndorserContactNumberChange(
                          "endorser_contact_number",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Current Status"
                      name="current_status"
                      value={jobOrder.current_status || ""}
                      onChange={(e) =>
                        handleCurrentStatusChange("current_status", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="heavily_infested">Heavily Infested</MenuItem>
                      <MenuItem value="medium_infestation">Medium Infestation</MenuItem>
                      <MenuItem value="light_infestation">Light Infestation</MenuItem>
                      <MenuItem value="for_inspection_first_before_treament">
                        For Inspection First Before Treament
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Payment Method"
                      name="payment_method"
                      value={jobOrder.payment_method || ""}
                      onChange={(e) =>
                        handlePaymentMethodChange("payment_method", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="COD">COD</MenuItem>
                      <MenuItem value="For Billing">For Billing</MenuItem>
                      <MenuItem value="Check collection">Check collection</MenuItem>
                      <MenuItem value="Bank deposit">Bank deposit</MenuItem>
                      <MenuItem value="Online/Gcash">Online/Gcash</MenuItem>
                      <MenuItem value="Paymongo">Paymongo</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Schedule Confirmed with Clients"
                      name="schedule_confirmed"
                      value={jobOrder.schedule_confirmed || ""}
                      onChange={(e) =>
                        handleScheduleConfirmedChange("schedule_confirmed", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="With Client Complaints"
                      name="client_complaints"
                      value={jobOrder.client_complaints || ""}
                      onChange={(e) =>
                        handleClientComplaintsChange("client_complaints", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>

                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Required Documents to Bring
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...DOCUMENTS_TO_BRING]
                  .sort((a, b) => a.localeCompare(b))                
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={jobOrder.documents_to_bring.includes(formattedOption)}
                            onChange={(e) => handleDocumentsToBringChange(e, formattedOption)}
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Equipment to Bring
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...EQUIPMENT]
                  .sort((a, b) => a.localeCompare(b))                
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={jobOrder.equipment.includes(formattedOption)}
                            onChange={(e) => handleEquipmentChange(e, formattedOption)}
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                marginRight: "5px"
              }}
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Technicians
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Button variant="outlined" color="primary" onClick={handleOpenModal}>
                      Assign Technicians
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    {(() => {
                      // Convert the startDate to a weekday name (e.g., Monday, Tuesday, etc.)
                      const startDateObj = new Date(jobOrder.job_date);
                      const options = { weekday: "long" };
                      const startDayName = startDateObj.toLocaleDateString("en-US", options);

                      return (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Array.isArray(jobOrder.technicians) && jobOrder.technicians.length > 0 ? (
                              jobOrder.technicians.map((tech) => {
                                // Find the full technician details from the global "technicians" array using the technician id
                                const fullTech = technicians.find((t) => t.id === tech.id);
                                // Compute the basic notes from competency levels
                                const basicNotes = [];
                                if (
                                  fullTech &&
                                  fullTech.competency_level &&
                                  fullTech.competency_level.driving_4_wheels &&
                                  fullTech.competency_level.driving_4_wheels !== "N/A"
                                ) {
                                  basicNotes.push("Driver: 4 wheels");
                                }
                                if (
                                  fullTech &&
                                  fullTech.competency_level &&
                                  fullTech.competency_level.driving_2_wheels &&
                                  fullTech.competency_level.driving_2_wheels !== "N/A"
                                ) {
                                  basicNotes.push("Driver: 2 wheels");
                                }

                                return (
                                  <TableRow key={tech.id}>

                                    <TableCell>{`${tech.lastname}, ${tech.firstname}`}</TableCell>
                                    <TableCell>
                                      {basicNotes.join(", ")}
                                      {fullTech && fullTech.rest_day && fullTech.rest_day.includes(startDayName) && (
                                        <span style={{ display: "block", color: "red" }}>Restday</span>
                                      )}
                                      {jobOrder?.job_date ? (
                                        fullTech &&
                                        (fullTech.off_service || [])
                                          .filter((off) => off.date === jobOrder.job_date)
                                          .map((off, index) => (
                                            <span key={index} style={{ display: "block", color: "red" }}>
                                              {off.reason.charAt(0).toUpperCase() + off.reason.slice(1)}
                                            </span>
                                          ))
                                      ) : (
                                        <div style={{ display: "block", color: "red" }}>
                                          No Job Order Date. Can't check rest day.
                                        </div>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3}>No technicians available</TableCell>
                              </TableRow>
                            )}

                          </TableBody>
                        </Table>
                      );
                    })()}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Dialog open={modalOpen} onClose={handleDialogClose}>
            <DialogTitle
              sx={{
                fontSize: "1.25rem",
                fontWeight: "bold",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
                mb: 2,
              }}
            >
              Assign Technicians
            </DialogTitle>
            <DialogContent>
              {(() => {
                // Convert the startDate to a weekday name (e.g., Monday, Tuesday, etc.)
                const startDateObj = new Date(jobOrder.job_date);
                const options = { weekday: "long" };
                const startDayName = startDateObj.toLocaleDateString("en-US", options);

                return (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>Name</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(technicians) && technicians.length > 0 ? (
                        technicians.map((tech) => {
                          const notes = [];
                          if (
                            tech.competency_level.driving_4_wheels &&
                            tech.competency_level.driving_4_wheels !== "N/A"
                          ) {
                            notes.push("Driver: 4 wheels");
                          }
                          if (
                            tech.competency_level.driving_2_wheels &&
                            tech.competency_level.driving_2_wheels !== "N/A"
                          ) {
                            notes.push("Driver: 2 wheels");
                          }
                          return (
                            <TableRow key={tech.id}>
                              <TableCell>
                                <Checkbox
                                  size="small"
                                  checked={selectedTechIds.includes(tech.id)}
                                  // Stop propagation so that clicking the checkbox doesn't trigger the dialog's onClose
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={handleTechCheckboxChange(tech.id)}
                                />
                              </TableCell>
                              <TableCell>{`${tech.lastname}, ${tech.firstname}`}</TableCell>
                              <TableCell>
                                {notes.join(", ")}
                                {tech.rest_day && tech.rest_day.includes(startDayName) && (
                                  <span style={{ display: "block", color: "red" }}>Restday</span>
                                )}
                                {jobOrder?.job_date ? (
                                  // Ensure tech.off_service is an array before mapping
                                  (tech.off_service || [])
                                    .filter((off) => off.date === jobOrder.job_date)
                                    .map((off, index) => (
                                      <span key={index} style={{ display: "block", color: "red" }}>
                                        {off.reason.charAt(0).toUpperCase() + off.reason.slice(1)}
                                      </span>
                                    ))
                                ) : (
                                  <div style={{ display: "block", color: "red" }}>No Job Order Date. Can't check rest day.</div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3}>No technicians available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleTechAssign} sx={{ fontSize: "1rem" }} variant="contained">
                Assign
              </Button>
              <Button onClick={handleCloseAssignment} sx={{ fontSize: "1rem" }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Form Type Section */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="h6">Form Type</Typography>}
                sx={{ padding: 0 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Form Type"
                      name="form_type"
                      value={jobOrder.form_type}
                      onChange={(e) =>
                        setJobOrder((prev) => ({ ...prev, form_type: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="makati_gpc_form">Makati GPC Form</MenuItem>
                      <MenuItem value="qc_form">QC Form</MenuItem>
                      <MenuItem value="termite_control_form">Termite Control Form</MenuItem>
                      <MenuItem value="transmittal_form">Transmittal Form</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Cancel and Submit Buttons */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-job-orders")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Oops—please fix these:</AlertTitle>
            <List dense disablePadding>
              {errors.map((msg, i) => (
                <ListItem key={i} sx={{ display: 'list-item', pl: 2 }}>
                  {msg}
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </form>
    </BaseFormWrapper>  
  );
}

export default CreateJobOrder;
